import { supabase } from '@/integrations/supabase/client';

export type Periodo = '7d' | '30d' | '90d';

// Types inline so the hook file can re-export them for consumers
export interface MetricasFinanceiras {
  faturamentoTotal: number;
  ticketMedio: number;
  totalOS: number;
  osConcluidas: number;
}

export interface ServicoMaisVendido {
  servicoNome: string;
  quantidade: number;
  valorTotal: number;
  percentual: number;
}

export interface FaturamentoPorPeriodo {
  data: string;
  faturamento: number;
  quantidade: number;
}

// Raw row types returned by fetchers
export interface OSConcluidaValor {
  id: string;
  valor_total: number | string | null;
  status: string | null;
}

export interface ItemOSVendido {
  servico_nome: string;
  quantidade: number | string | null;
  valor_total: number | string | null;
}

export interface OSFaturamentoRow {
  id: string;
  valor_total: number | string | null;
  data_fim_execucao: string | null;
}

// ============================================================================
// Helpers — pure
// ============================================================================

export function getDataInicio(periodo: Periodo, now: Date = new Date()): Date {
  const dataInicio = new Date(now);
  const diasAtras = periodo === '7d' ? 7 : periodo === '30d' ? 30 : 90;
  dataInicio.setDate(dataInicio.getDate() - diasAtras);
  dataInicio.setHours(0, 0, 0, 0);
  return dataInicio;
}

// ============================================================================
// Fetchers — Supabase-touching, throw on error
// ============================================================================

export async function fetchOSConcluidasNoPeriodo(dataInicio: Date): Promise<OSConcluidaValor[]> {
  const { data, error } = await supabase
    .from('ordens_servico')
    .select('id, valor_total, status')
    .eq('status', 'concluida')
    .gte('data_fim_execucao', dataInicio.toISOString());
  if (error) throw error;
  return data ?? [];
}

export async function fetchTotalOSNoPeriodo(dataInicio: Date): Promise<number> {
  const { count, error } = await supabase
    .from('ordens_servico')
    .select('id', { count: 'exact', head: true })
    .gte('criado_em', dataInicio.toISOString());
  if (error) throw error;
  return count ?? 0;
}

export async function fetchItensOSConcluidas(dataInicio: Date): Promise<ItemOSVendido[]> {
  const { data, error } = await supabase
    .from('ordem_servico_itens')
    .select(`
      servico_nome,
      quantidade,
      valor_total,
      ordem_servico:ordens_servico!inner(
        id,
        status,
        data_fim_execucao
      )
    `)
    .eq('ordem_servico.status', 'concluida')
    .gte('ordem_servico.data_fim_execucao', dataInicio.toISOString());
  if (error) throw error;
  return (data ?? []) as unknown as ItemOSVendido[];
}

export async function fetchFaturamentoRows(dataInicio: Date): Promise<OSFaturamentoRow[]> {
  const { data, error } = await supabase
    .from('ordens_servico')
    .select('id, valor_total, data_fim_execucao')
    .eq('status', 'concluida')
    .gte('data_fim_execucao', dataInicio.toISOString())
    .order('data_fim_execucao', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

// ============================================================================
// Aggregators — pure, no side effects
// ============================================================================

export function calcularMetricasFinanceiras(
  osConcluidas: OSConcluidaValor[],
  totalOS: number,
): MetricasFinanceiras {
  const osCount = osConcluidas.length;
  const faturamentoTotal = osConcluidas.reduce(
    (acc, os) => acc + (Number(os.valor_total) || 0),
    0,
  );
  const ticketMedio = osCount > 0 ? faturamentoTotal / osCount : 0;

  return {
    faturamentoTotal,
    ticketMedio: Math.round(ticketMedio * 100) / 100,
    totalOS,
    osConcluidas: osCount,
  };
}

export function calcularServicosMaisVendidos(
  itens: ItemOSVendido[],
  limite: number,
): ServicoMaisVendido[] {
  const porServico: Record<string, { quantidade: number; valorTotal: number }> = {};

  itens.forEach(item => {
    const nome = item.servico_nome;
    if (!porServico[nome]) {
      porServico[nome] = { quantidade: 0, valorTotal: 0 };
    }
    porServico[nome].quantidade += Number(item.quantidade) || 0;
    porServico[nome].valorTotal += Number(item.valor_total) || 0;
  });

  const valorTotalGeral = Object.values(porServico).reduce((acc, s) => acc + s.valorTotal, 0);

  return Object.entries(porServico)
    .map(([servicoNome, dados]) => ({
      servicoNome,
      quantidade: dados.quantidade,
      valorTotal: dados.valorTotal,
      percentual: valorTotalGeral > 0 ? Math.round((dados.valorTotal / valorTotalGeral) * 100) : 0,
    }))
    .sort((a, b) => b.valorTotal - a.valorTotal)
    .slice(0, limite);
}

export function agruparFaturamentoPorDia(
  ordens: OSFaturamentoRow[],
  periodo: Periodo,
  now: Date = new Date(),
): FaturamentoPorPeriodo[] {
  const porData: Record<string, { faturamento: number; quantidade: number }> = {};

  ordens.forEach(os => {
    if (os.data_fim_execucao) {
      const dataStr = new Date(os.data_fim_execucao).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
      });
      if (!porData[dataStr]) {
        porData[dataStr] = { faturamento: 0, quantidade: 0 };
      }
      porData[dataStr].faturamento += Number(os.valor_total) || 0;
      porData[dataStr].quantidade++;
    }
  });

  const resultado: FaturamentoPorPeriodo[] = [];
  const diasAtras = periodo === '7d' ? 7 : periodo === '30d' ? 30 : 90;

  for (let i = diasAtras - 1; i >= 0; i--) {
    const data = new Date(now);
    data.setDate(data.getDate() - i);
    const dataStr = data.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
    });
    resultado.push({
      data: dataStr,
      faturamento: porData[dataStr]?.faturamento || 0,
      quantidade: porData[dataStr]?.quantidade || 0,
    });
  }

  return resultado;
}
